import { Routes } from '@angular/router';
import { dataMappingRoutes } from '@data-mapping/data-mapping.routes';
import { dataImportRoutes } from '@app/leasing-company/data-import/data-import.routes';
import { userAuthorizationsRoutes } from '@app/leasing-company/user-authorizations/user-authorizations.routes';
import { packsRoutes } from '@app/leasing-company/packs/packs.routes';
import { leasingContractsRoutes } from '@app/leasing-company/leasing-contracts/leasing-contracts.routes';
import { coulanceRoutes } from '@app/leasing-company/coulance/coulance.routes';
import { SupplierGroupsOverviewStateService } from '@app/leasing-company/supplier-groups/overview/state/supplier-groups-overview-state.service';
import { SuppliersOverviewStateService } from '@app/leasing-company/suppliers/overview/state/suppliers-overview-state.service';
import { ActivityGroupReductionsOverviewStateService } from '@app/leasing-company/activity-group-reductions/overview/state/activity-group-reductions-overview-state.service';
import { isLeasingCompanyUserGuard } from '@shared/guards/leasing-company/leasing-company.guard';
import { isFeatureEnabled } from '@core/feature-flag/feature-flag.guard';
import { ActivityGroupReductionAddFormService } from '@app/leasing-company/activity-group-reductions/add/state/activity-group-reduction-add-form.service';
import { LeasingCompanyThresholdsOverviewStateService } from '@app/leasing-company/leasing-company-thresholds/overview/state/leasing-company-thresholds-overview-state.service';
import { LinkedActivitiesOverviewStateService } from '@app/leasing-company/maintenance-intervals/linked-activities/overview/state/linked-activities-overview-state.service';
import { lockWorkOrderGuard } from '@app/leasing-company/work-orders/guard/lock-work-order.guard';
import { attachmentsUploadingWarningGuard } from '@shared/guards/attachments-uploading-warning/attachments-uploading-warning.guard';
import { isEditingGuard } from '@app/leasing-company/work-orders/pages/details/components/brand-activity-mappings/guard/is-editing.guard';
import { DoubleMaintenanceOverviewStateService } from '@app/leasing-company/maintenance-intervals/double-maintenance/overview/state/double-maintenance-overview-state.service';
import { unsavedWorkOrderChangesGuard } from '@shared/guards/unsaved-work-order-changes/unsaved-work-order-changes.guard';
import { UserAuthorizationsOverviewStateService } from '@app/leasing-company/user-authorizations/overview/state/user-authorizations-overview-state.service';
import { CustomCommentsOverviewStateService } from '@app/leasing-company/custom-comments/overview/state/custom-comments-overview-state.service';

export const leasingCompanyRoutes: Routes =
  [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'work-orders',
    },
    {
      path: 'data-mapping',
      children: dataMappingRoutes,
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        isFeatureEnabled('leasing-company.data-mapping'),
      ],
    },
    {
      path: 'data-import',
      children: dataImportRoutes,
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        isFeatureEnabled('leasing-company.data-import'),
      ],
    },
    {
      path: 'user-authorizations',
      children: userAuthorizationsRoutes,
      providers: [UserAuthorizationsOverviewStateService],
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER'] }),
        isFeatureEnabled('leasing-company.user-authorizations'),
      ],
    },
    {
      path: 'administration',
      loadComponent: () => import('@app/leasing-company/administration/administration.component').then(component => component.AdministrationComponent),
      canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
      children: [
        {
          path: 'overview',
          loadComponent: () => import('@app/leasing-company/administration/administration-overview/administration-overview.component').then(component => component.AdministrationOverviewComponent),
        },
        {
          path: 'settings',
          loadComponent: () => import('@app/leasing-company/administration/settings/settings.component').then(component => component.SettingsComponent),
        },
        {
          path: 'settings/edit',
          loadComponent: () => import('@app/leasing-company/administration/settings/components/edit/settings-edit.component').then(component => component.SettingsEditComponent),
        },
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'overview',
        },
      ],
    },
    {
      path: 'supplier-groups',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/supplier-groups/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [SupplierGroupsOverviewStateService],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/supplier-groups/add/add.component').then((component) => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':supplierGroupId',
          loadComponent: () => import('@app/leasing-company/supplier-groups/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':supplierGroupId/edit',
          loadComponent: () => import('@app/leasing-company/supplier-groups/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
      canActivate: [isFeatureEnabled('leasing-company.supplier-groups')],
    },
    {
      path: 'work-orders',
      canActivate: [isFeatureEnabled('leasing-company.work-orders', '/leasing-company/brand-thresholds')],
      children: [
        {
          path: '',
          loadComponent: () => import('./work-orders/pages/overview/overview.component').then(component => component.OverviewComponent),
        },
        {
          path: ':id',
          canActivate: [lockWorkOrderGuard],
          canDeactivate: [unsavedWorkOrderChangesGuard],
          loadComponent: () => import('./work-orders/pages/details/details.component'),
          children: [
            {
              path: '',
              pathMatch: 'full',
              redirectTo: 'activities',
            },
            {
              path: 'activities',
              loadComponent: () => import('./work-orders/pages/details/components/activities/activities.component').then(component => component.WorkOrderActivitiesComponent),
            },
            {
              path: 'brand-activity-mappings',
              loadComponent: () => import('./work-orders/pages/details/components/brand-activity-mappings/brand-activity-mappings.component').then(component => component.BrandActivityMappingsComponent),
              canActivate: [isFeatureEnabled('leasing-company.work-order.activity.manage-brand-mappings')],
              canDeactivate: [isEditingGuard()],
            },
            {
              path: 'details',
              loadComponent: () => import('./work-orders/pages/details/components/details/details.component').then(component => component.DetailsComponent),
            },
            {
              path: 'vehicle-history',
              loadComponent: () => import('./work-orders/pages/details/components/vehicle-history/vehicle-history.component').then(component => component.VehicleHistoryComponent),
            },
            {
              path: 'documents',
              loadComponent: () => import('./work-orders/pages/details/components/attachments/attachments.component').then(component => component.AttachmentsComponent),
              canDeactivate: [attachmentsUploadingWarningGuard],
            },
          ],
        },
      ],
    },
    {
      path: 'replacement-transports/:id',
      loadComponent: () => import('@app/leasing-company/replacement-transports/replacement-transport-request.component'),
    },
    {
      path: 'suppliers',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/suppliers/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [SuppliersOverviewStateService],
        },
        {
          path: ':supplierId',
          loadComponent: () => import('@app/leasing-company/suppliers/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':supplierId/edit',
          loadComponent: () => import('@app/leasing-company/suppliers/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
      canActivate: [isFeatureEnabled('leasing-company.suppliers')],
    },
    {
      path: 'maintenance-intervals',
      loadComponent: () => import('@app/leasing-company/maintenance-intervals/main-intervals.component').then(component => component.MainIntervalsComponent),
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/maintenance-intervals/double-maintenance/overview/overview.component').then(component => component.OverviewComponent),
          providers: [DoubleMaintenanceOverviewStateService],
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/maintenance-intervals/double-maintenance/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'linked-activities',
          children: [
            {
              path: '',
              loadComponent: () => import('@app/leasing-company/maintenance-intervals/linked-activities/overview/overview.component').then(component => component.OverviewComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
              providers: [LinkedActivitiesOverviewStateService],
            },
            {
              path: 'add',
              loadComponent: () => import('@app/leasing-company/maintenance-intervals/linked-activities/add/add.component').then(component => component.AddComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
          ],
        },
        {
          path: ':doubleMaintenanceId',
          loadComponent: () => import('@app/leasing-company/maintenance-intervals/double-maintenance/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':doubleMaintenanceId/edit',
          loadComponent: () => import('@app/leasing-company/maintenance-intervals/double-maintenance/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
      canActivate: [isFeatureEnabled('leasing-company.maintenance-intervals')],
    },
    {
      path: 'packs',
      children: packsRoutes,
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        isFeatureEnabled('leasing-company.packs'),
      ],
    },
    {
      path: 'leasing-contracts',
      children: leasingContractsRoutes,
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        isFeatureEnabled('leasing-company.leasing-contracts'),
      ],
    },
    {
      path: 'leasing-company-thresholds',
      canActivate: [isFeatureEnabled('leasing-company.leasing-company-thresholds')],
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/leasing-company-thresholds/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [LeasingCompanyThresholdsOverviewStateService],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/leasing-company-thresholds/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':leasingCompanyThresholdId',
          loadComponent: () => import('@app/leasing-company/leasing-company-thresholds/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':leasingCompanyThresholdId/edit',
          loadComponent: () => import('@app/leasing-company/leasing-company-thresholds/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'activity-group-reductions',
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        isFeatureEnabled('leasing-company.activity-group-reductions'),
      ],
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/activity-group-reductions/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [ActivityGroupReductionsOverviewStateService],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/activity-group-reductions/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [ActivityGroupReductionAddFormService]
        },
        {
          path: ':activityGroupReductionId',
          loadComponent: () => import('@app/leasing-company/activity-group-reductions/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':activityGroupReductionId/edit',
          loadComponent: () => import('@app/leasing-company/activity-group-reductions/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'activity-settings',
      canActivate: [
        isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] }),
        // isFeatureEnabled('leasing-company.activity-settings')
      ],
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/activity-settings/overview/overview.component').then((component) => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'vehicle-types',
      canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/vehicle-types/overview/overview.component').then(component => component.OverviewComponent),
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/vehicle-types/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':id',
          loadComponent: () => import('@app/leasing-company/vehicle-types/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':id/edit',
          loadComponent: () => import('@app/leasing-company/vehicle-types/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'disapproval-reasons',
      loadComponent: () => import('@app/leasing-company/disapproval-reason/disapproval-reason.component').then(component => component.DisapprovalReasonComponent),
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/disapproval-reason/overview/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'activity-exceptions',
          children: [
            {
              path: '',
              loadComponent: () => import('@app/leasing-company/disapproval-reason/activity-exceptions/overview/overview.component').then(component => component.OverviewComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
          ],
        },
        {
          path: ':disapprovalReasonId',
          children: [
            {
              path: '',
              loadComponent: () => import('@app/leasing-company/disapproval-reason/overview/details/details.component').then(component => component.DetailsComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
            {
              path: 'edit',
              loadComponent: () => import('@app/leasing-company/disapproval-reason/overview/edit/edit.component').then(component => component.EditComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
          ],
        },
        {
          path: '**',
          redirectTo: 'disapproval-reasons',
        },
      ],
    },
    {
      path: 'disapproval-reason-thresholds',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/disapproval-reason-threshold/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'preferred-tyre-brands',
      canActivate: [isFeatureEnabled('leasing-company.preferred-tyre-brands')],
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/preferred-tyre-brand/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/preferred-tyre-brand/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':preferredTyreBrandId',
          loadComponent: () => import('@app/leasing-company/preferred-tyre-brand/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':preferredTyreBrandId/edit',
          loadComponent: () => import('@app/leasing-company/preferred-tyre-brand/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'custom-comments',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/custom-comments/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
          providers: [CustomCommentsOverviewStateService],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/custom-comments/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':customCommentId',
          loadComponent: () => import('@app/leasing-company/custom-comments/detail/detail.component').then(component => component.DetailComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':customCommentId/edit',
          loadComponent: () => import('@app/leasing-company/custom-comments/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'rental-classes',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/rental-classes/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':rentalClassCode',
          loadComponent: () => import('@app/leasing-company/rental-classes/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':rentalClassCode/edit',
          loadComponent: () => import('@app/leasing-company/rental-classes/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'brand-thresholds',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/brand-threshold/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/brand-threshold/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':brandThresholdId',
          loadComponent: () => import('@app/leasing-company/brand-threshold/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':brandThresholdId/edit',
          loadComponent: () => import('@app/leasing-company/brand-threshold/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'tyre-maintenances',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/tyre-maintenance/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/tyre-maintenance/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':tyreMaintenanceId',
          loadComponent: () => import('@app/leasing-company/tyre-maintenance/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':tyreMaintenanceId/edit',
          loadComponent: () => import('@app/leasing-company/tyre-maintenance/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'tyre-brand-discounts',
      canActivate: [isFeatureEnabled('leasing-company.tyre-brand-discounts')],
      loadComponent: () => import('@app/leasing-company/tyre-brand-discount/tyre-brand-discount-parent-overview.component')
        .then(component => component.TyreBrandDiscountParentOverviewComponent),
      children: [
        {
          path: 'general',
          children: [
            {
              path: '',
              loadComponent: () => import('@app/leasing-company/tyre-brand-discount/general/overview/overview.component').then(component => component.OverviewComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
            {
              path: 'add',
              loadComponent: () => import('@app/leasing-company/tyre-brand-discount/general/add/add.component').then(component => component.AddComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
          ],
        },
        {
          path: 'supplier-group',
          children: [
            {
              path: '',
              loadComponent: () => import('@app/leasing-company/tyre-brand-discount/supplier-groups/overview/overview.component').then(component => component.OverviewComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
            {
              path: 'add',
              loadComponent: () => import('@app/leasing-company/tyre-brand-discount/supplier-groups/add/add.component').then(component => component.AddComponent),
              canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
            },
          ],
        },
        {
          path: '**',
          redirectTo: 'general',
        },
      ],
    },
    {
      path: 'coulance',
      loadComponent: () => import('@app/leasing-company/coulance/coulance-parent-overview.component')
        .then(component => component.CoulanceParentOverviewComponent),
      children: coulanceRoutes,
      canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
    },
    {
      path: 'leasing-contract-groups',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/leasing-contract-group/overview/overview.component').then(component => component.OverviewComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'add',
          loadComponent: () => import('@app/leasing-company/leasing-contract-group/add/add.component').then(component => component.AddComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: ':leasingContractGroupId',
          loadComponent: () => import('@app/leasing-company/leasing-contract-group/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
    {
      path: 'cost-centre-configuration',
      children: [
        {
          path: '',
          loadComponent: () => import('@app/leasing-company/cost-centre-configuration/details/details.component').then(component => component.DetailsComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
        {
          path: 'edit',
          loadComponent: () => import('@app/leasing-company/cost-centre-configuration/edit/edit.component').then(component => component.EditComponent),
          canActivate: [isLeasingCompanyUserGuard({ roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'] })],
        },
      ],
    },
  ];
